// Portfolio is intentionally anonymized.
// Real Kostaja work, presented by industry and by problem — not by client name.
// No fake metrics. No invented results. No stock photography.
// Slugs and titles are generic on purpose so the work speaks for the industry
// rather than the individual brand. Old brand-named slugs are redirected in
// /app/frontend/src/data/redirects.js.

export const PORTFOLIO = [
  {
    slug: "multi-service-auto-shop",
    title: "Bristol Auto",
    client: "Bristol Auto Performance",
    logo: "/portfolio/logos/bristol-auto-performance.png",
    logoBg: "#000000",
    logoInk: "rgba(255,255,255,0.6)",
    type: "Client Work",
    category: "Automotive",
    industry: "automotive",
    summary:
      "How a multi-service auto shop finally started looking like one business across bays, print, social, and search, so customers stopped calling around for a second opinion.",
    story: {
      problem:
        "The shop had grown from mechanical repair into detailing, tire, and body work, and every service line had its own accidental look. Customers routinely thought they were dealing with three different businesses. Referrals from one service to another kept getting lost because the shop never felt like one operation.",
      sequence: [
        "Audited how each service line was presenting itself online, in the bay, and on paper.",
        "Rebuilt the website around service categories, with a dedicated page per repair type and clear next steps.",
        "Standardized the visual system so signage, print handouts, social, and Google Business Profile all read as the same shop.",
        "Layered a light social and Google Business Profile rhythm the front-of-house team could actually maintain.",
      ],
      result:
        "The shop stopped losing referrals between service lines and started earning them. Customers who came in for tires began asking about detailing on the same visit. The operator no longer has to explain, in every call, that all the services are under the same roof.",
    },
    cover: "/portfolio/placeholders/multi-service-auto-shop.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Website Direction",
      "Service Presentation",
      "Social Media Creative",
      "Print and Sales Materials",
      "Campaign Support",
      "Operational Marketing Support",
    ],
    relatedServices: [
      "website-growth-build",
      "local-visibility-starter-kit",
      "social-profile-kit",
      "one-page-sales-sheet",
      "google-business-profile-optimization",
    ],
    relatedIndustries: ["automotive", "local-business"],
    relatedNeeds: [
      "better-website",
      "local-visibility",
      "sales-materials",
      "content-system",
      "portfolio-or-proof",
    ],
  },
  {
    slug: "real-estate-marketing-subbrand",
    title: "LotWise Home",
    client: "LotWise Home",
    logo: "/portfolio/logos/lotwise-home.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "Real Estate",
    industry: "real-estate",
    summary:
      "How a real estate marketing subbrand went from side project to a system agents, brokers, and mortgage partners actually route business through.",
    story: {
      problem:
        "The original concept was a personal side project doing real estate marketing on the weekends. As referrals grew, it was time to give it a working brand, a working website, and a working intake path, so it could hold serious partners without looking like a side hustle.",
      sequence: [
        "Named the subbrand and defined the two-audience positioning: agents on one side, partners on the other.",
        "Built a full brand system with rules that scaled across print, digital, and partner-facing materials.",
        "Structured the website around service packages, not deliverables, so buyers could see the fit in one page.",
        "Prepared partnership materials for brokerages, mortgage professionals, and referral sources.",
      ],
      result:
        "The subbrand went from an inconsistent side project to a working system with a real front door. Agents and mortgage partners now route business through it without the founder having to explain the offer on every call.",
    },
    cover: "/portfolio/placeholders/real-estate-marketing-subbrand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand System",
      "Website Direction",
      "Real Estate Marketing Positioning",
      "Service Packaging",
      "Partnership Materials",
    ],
    relatedServices: [
      "brand-foundation-complete",
      "website-system-complete",
      "brand-and-website-growth-system",
      "pitch-deck-refinement",
      "social-profile-kit",
    ],
    relatedIndustries: ["real-estate", "professional-services", "financial-services"],
    relatedNeeds: [
      "stronger-brand",
      "better-website",
      "sales-materials",
      "content-system",
      "strategy-before-deciding",
    ],
  },
  {
    slug: "independent-mortgage-broker",
    title: "Murray Mortgages",
    client: "Murray Mortgages",
    logo: "/portfolio/logos/murray-mortgages.webp",
    logoBg: "#0F2A44",
    logoInk: "rgba(255,255,255,0.55)",
    type: "Client Work",
    category: "Professional Services",
    industry: "financial-services",
    summary:
      "How an independent mortgage broker started attracting the referrals he already deserved by looking as trustworthy online as he sounds on the phone.",
    story: {
      problem:
        "The broker had years of quiet referrals and warm reviews from realtors. His online presence did not reflect any of that. Prospects who Googled him after a referral often paused at the outdated social profile and generic photo, and some never called back.",
      sequence: [
        "Cleaned up the personal brand direction so it read as senior, safe, and current.",
        "Redesigned the social profile and headshot presentation across LinkedIn, Facebook, and Instagram.",
        "Wrote a plain-English one-page introduction that realtors could forward to prospects.",
        "Standardized how his name, headshot, and value proposition appeared everywhere he was searchable.",
      ],
      result:
        "The gap between the reputation and the online first impression closed. Realtor referrals started converting to booked calls at a much better rate, and prospects arrived on those calls already trusting him.",
    },
    cover: "/portfolio/placeholders/independent-mortgage-broker.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand Presence",
      "Social Profile Creative",
      "Content Direction",
      "Professional First Impression",
    ],
    relatedServices: [
      "social-profile-kit",
      "brand-audit",
      "business-card-design",
      "one-page-sales-sheet",
    ],
    relatedIndustries: ["financial-services", "professional-services", "real-estate"],
    relatedNeeds: [
      "stronger-brand",
      "professional-first-impression",
      "social-media-support",
      "sales-materials",
    ],
  },
  {
    slug: "local-restaurant-social",
    title: "Deano's Chicken",
    client: "Deano's Chicken",
    logo: "/portfolio/logos/deanos-chicken.jpg",
    logoBg: "#000000",
    logoInk: "rgba(255,255,255,0.6)",
    type: "Client Work",
    category: "Food and Beverage",
    industry: "food-and-beverage",
    summary:
      "How a neighbourhood restaurant turned quiet weekdays and slow specials into a steady social rhythm that pulls locals in without discounting the menu.",
    story: {
      problem:
        "The restaurant had a great regular crowd on Fridays and Saturdays but empty tables Monday through Wednesday. The team was posting on social when things were slow, which meant posting the least on the nights the restaurant needed help most.",
      sequence: [
        "Designed a light weekly social rhythm the team could actually stick to, on a set day and time.",
        "Built a small library of on-brand templates for weekday specials, community mentions, and behind-the-scenes moments.",
        "Repositioned weekday content to lead with the room and the food, not with discount codes.",
        "Aligned the Google Business Profile posts and photos with the same weekly rhythm.",
      ],
      result:
        "Weekday covers steadied without the restaurant training customers to expect discounts. Locals who used to only come on weekends began showing up mid-week because the feed made the room feel alive on those nights.",
    },
    cover: "/portfolio/placeholders/local-restaurant-social.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Social Media Creative",
      "Local Offer Promotion",
      "Food and Beverage Campaign Support",
      "Community Messaging",
    ],
    relatedServices: [
      "social-profile-kit",
      "flyer-design",
      "menu-design",
      "local-visibility-starter-kit",
    ],
    relatedIndustries: ["food-and-beverage", "restaurants-and-bars", "local-business"],
    relatedNeeds: [
      "social-media-support",
      "local-customers",
      "print-materials",
      "local-visibility",
    ],
  },
  {
    slug: "seasonal-food-stand-brand",
    title: "Wings N Things",
    client: "Wings N Things",
    logo: "/portfolio/logos/wings-n-things.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Client Work",
    category: "Food and Beverage",
    industry: "food-and-beverage",
    summary:
      "How a small seasonal food business built a brand people show up for, share, and remember from one season to the next.",
    story: {
      problem:
        "The operator ran a seasonal stand every summer that regulars loved but nobody else could find. There was no continuous brand identity between seasons. Every new location and new season felt like starting from scratch.",
      sequence: [
        "Built a brand direction that could travel between locations, seasons, and menu shifts without losing recognition.",
        "Designed simple, printable menu boards, chalk-friendly signage, and event flyers.",
        "Created a small library of social posts the operator could ship from a phone during the season.",
        "Standardized how the stand appeared on Google Business Profile and in event listings.",
      ],
      result:
        "The stand now feels like a returning brand rather than a new business every summer. Regulars share it, event organizers reuse it, and the operator can open a new location without redoing the identity from scratch.",
    },
    cover: "/portfolio/placeholders/seasonal-food-stand-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand Direction",
      "Social Media Creative",
      "Local Promotion",
      "Menu and Offer Presentation",
    ],
    relatedServices: [
      "brand-foundation-core",
      "social-profile-kit",
      "menu-design",
      "flyer-design",
    ],
    relatedIndustries: ["food-and-beverage", "restaurants-and-bars", "local-business"],
    relatedNeeds: [
      "stronger-brand",
      "social-media-support",
      "local-customers",
      "print-materials",
    ],
  },
  {
    slug: "talent-and-recruitment-brand",
    title: "TalentRoute",
    client: "TalentRoute",
    logo: "/portfolio/logos/talentroute.jpg",
    logoBg: "#0F2A44",
    logoInk: "rgba(255,255,255,0.55)",
    type: "Owned Brand",
    category: "Brand System",
    industry: "professional-services",
    summary:
      "How a candidate-first staffing brand was built to feel senior enough for employers and safe enough for the people they place.",
    story: {
      problem:
        "The founder wanted to launch a staffing service that treated candidates like people, not like inventory. That story is easy to say and hard to signal. Every recruiter says they care about candidates. The brand had to make that promise feel real before the first conversation.",
      sequence: [
        "Wrote a positioning that named the two audiences (employer, candidate) as one commitment, not two.",
        "Designed a brand system that read senior for employers and calm for candidates.",
        "Structured the website around the promise, with dedicated paths for both audiences.",
        "Prepared launch materials: candidate one-pager, employer sales sheet, and a set of social launch pieces.",
      ],
      result:
        "The brand launched with the two audiences clearly served by one voice. Employers took the pitch calls seriously. Candidates trusted the process enough to submit before ever meeting the founder in person.",
    },
    cover: "/portfolio/placeholders/talent-and-recruitment-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand System",
      "Website Direction",
      "Service Architecture",
      "Messaging System",
      "Launch Materials",
    ],
    relatedServices: [
      "brand-foundation-complete",
      "website-system-complete",
      "brand-and-website-growth-system",
      "one-page-sales-sheet",
    ],
    relatedIndustries: ["professional-services", "technology", "startups"],
    relatedNeeds: [
      "stronger-brand",
      "better-website",
      "launch-plan",
      "sales-materials",
    ],
  },
  {
    slug: "ai-receptionist-brand",
    title: "OpenLine",
    client: "OpenLine",
    logo: "/portfolio/logos/openline.jpg",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "AI and Automation",
    industry: "professional-services",
    summary:
      "How an AI receptionist concept was packaged so service businesses could see the missed-call problem, the fix, and the value without a demo call.",
    story: {
      problem:
        "The AI receptionist idea was compelling in demo but hard to explain in writing. Prospects arrived intrigued, hit a wall on the value question, and asked for a demo before they had understood the offer. The team needed the site to sell the concept before the demo call, not during it.",
      sequence: [
        "Reframed the offer around the buyer's problem (missed calls, lost leads) instead of the technology (AI, voice, LLM).",
        "Built a brand identity that read serious and dependable, not gimmicky.",
        "Structured the website around three pages: the problem, the fix, and the outcome.",
        "Packaged the offer into named tiers with plain-English inclusions so buyers could self-qualify.",
      ],
      result:
        "The concept could finally be understood without a demo call. Demo calls now open with buyers who already know what they are looking at, which shortens the sales cycle and raises the quality of the questions.",
    },
    cover: "/portfolio/placeholders/ai-receptionist-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand System",
      "Service Positioning",
      "Website Direction",
      "Offer Packaging",
      "Sales Messaging",
    ],
    relatedServices: [
      "brand-foundation-complete",
      "website-growth-build",
      "one-page-sales-sheet",
      "pitch-deck-refinement",
    ],
    relatedIndustries: ["professional-services", "home-services", "technology"],
    relatedNeeds: [
      "more-leads",
      "sales-materials",
      "better-website",
      "digital-product-setup",
    ],
  },
  {
    slug: "home-services-booking-brand",
    title: "FixBook",
    client: "FixBook",
    logo: "/portfolio/logos/fixbook.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "AI and Automation",
    industry: "home-services",
    summary:
      "How a home services booking concept was framed so contractors could stop losing after-hours leads to whoever answers first.",
    story: {
      problem:
        "Contractors were losing after-hours calls to competitors that answered the phone at eight in the evening. The concept was a shared booking layer that caught those calls. It only worked if contractors could see, at a glance, that the leak was real and that the fix did not require a full software overhaul.",
      sequence: [
        "Framed the offer around one measurable business loss: the after-hours missed call.",
        "Built a brand direction that read contractor-first, not tech-first.",
        "Packaged the service into a small monthly commitment with a clear cancellation path.",
        "Wrote a one-page sales sheet contractors could hand to a partner or an office manager.",
      ],
      result:
        "Contractors could evaluate the concept in two minutes on a phone. The pitch stopped being a technology conversation and started being an operations conversation, which is the one contractors are already willing to have.",
    },
    cover: "/portfolio/placeholders/home-services-booking-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand Direction",
      "Offer Positioning",
      "Service Packaging",
      "Marketing Funnel Direction",
    ],
    relatedServices: [
      "brand-foundation-core",
      "website-growth-build",
      "local-visibility-starter-kit",
      "one-page-sales-sheet",
    ],
    relatedIndustries: ["home-services", "contractors", "local-business"],
    relatedNeeds: [
      "more-leads",
      "local-customers",
      "better-website",
      "sales-materials",
    ],
  },
  {
    slug: "dealership-support-brand",
    title: "LotWise Auto",
    client: "LotWise Auto",
    logo: "/portfolio/logos/lotwise-auto.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "Automotive",
    industry: "automotive",
    summary:
      "How a dealership support brand was built to give small dealers professional-looking inventory presentation without hiring an in-house marketing team.",
    story: {
      problem:
        "Small independent dealers were losing shoppers to larger dealer groups whose photos, listings, and social presence just looked more professional. Hiring an in-house marketing team was out of reach. The concept was a shared marketing layer, but it needed to feel bigger than a freelancer service.",
      sequence: [
        "Built a brand system that read as an operating partner, not a vendor.",
        "Packaged the offer into a monthly service that included photos, listing polish, and social presence.",
        "Structured the website so dealers could see the before/after difference on a real inventory example.",
        "Prepared sales positioning materials for one-on-one outreach to independent dealers.",
      ],
      result:
        "Independent dealers stopped looking like the underdog against larger dealer groups. The concept became easy to explain and easy to say yes to at a monthly price a small dealer could actually cover.",
    },
    cover: "/portfolio/placeholders/dealership-support-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand System",
      "Service Packaging",
      "Website Direction",
      "Sales Positioning",
      "Automotive Marketing System",
    ],
    relatedServices: [
      "brand-foundation-core",
      "website-growth-build",
      "one-page-sales-sheet",
      "local-service-page-package",
    ],
    relatedIndustries: ["automotive", "local-business"],
    relatedNeeds: [
      "stronger-brand",
      "better-website",
      "sales-materials",
      "portfolio-or-proof",
    ],
  },
  {
    slug: "capital-access-brand",
    title: "FundRoute",
    client: "FundRoute",
    logo: "/portfolio/logos/fundroute.png",
    logoBg: "#0B1220",
    logoInk: "rgba(255,255,255,0.55)",
    type: "Owned Brand",
    category: "Financial Services",
    industry: "financial-services",
    summary:
      "How a capital access concept was built to feel institutional enough for investors and clear enough for founders on the other side of the table.",
    story: {
      problem:
        "The concept sat between two audiences that speak different languages: institutional investors, who need signals of rigour, and founders, who need signals of clarity. Most brands in the space picked one side and lost the other. This one had to earn trust on both sides simultaneously.",
      sequence: [
        "Wrote positioning that named the two audiences explicitly and refused to pretend they were one.",
        "Built a brand system with two accents on one identity: institutional gravitas plus founder clarity.",
        "Directed a pitch deck and investor-facing materials that read serious without being sterile.",
        "Structured the website so both audiences could navigate to the version of the offer that fit them.",
      ],
      result:
        "The brand now holds up in investor rooms and in founder inboxes without shifting voice. Both sides read the same materials and see something that respects them, which is the harder half of raising and deploying capital.",
    },
    cover: "/portfolio/placeholders/capital-access-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand System",
      "Naming Direction",
      "Visual Identity Direction",
      "Investor-Facing Positioning",
    ],
    relatedServices: [
      "brand-foundation-complete",
      "pitch-deck-refinement",
      "brand-and-website-growth-system",
    ],
    relatedIndustries: ["financial-services", "professional-services", "startups"],
    relatedNeeds: [
      "stronger-brand",
      "professional-first-impression",
      "sales-materials",
      "strategy-before-deciding",
    ],
  },
  {
    slug: "operator-media-brand",
    title: "Operating Class",
    client: "Operating Class",
    logo: "/portfolio/logos/operating-class.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "Media and Publishing",
    industry: "media-and-publishing",
    summary:
      "How a business advisory concept was built to sound like a practical operator, not another content marketer, so the right readers stayed and the wrong ones self-filtered.",
    story: {
      problem:
        "The founder had a distinct operator voice that got lost when packaged as generic business content. Every draft came back sounding like every other advisor site. The brand needed to sound like the founder actually sounds in a room, not like a LinkedIn feed.",
      sequence: [
        "Wrote a voice guide that captured the founder's plain-English cadence and refused generic business phrases.",
        "Named the concept and set an editorial promise readers could opt in or out of quickly.",
        "Structured the site around long-form writing and a podcast, not lead magnets or courses.",
        "Prepared launch content: three long-form pieces and a first podcast series positioning.",
      ],
      result:
        "The right readers found the concept and stayed. Prospects who arrived from LinkedIn came in already knowing the voice they were going to work with, which meant fewer wasted discovery calls.",
    },
    cover: "/portfolio/placeholders/operator-media-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand Concept",
      "Media Positioning",
      "Content Architecture",
      "Podcast and Editorial Direction",
    ],
    relatedServices: [
      "brand-foundation-core",
      "website-growth-build",
      "digital-product-launch-support",
    ],
    relatedIndustries: ["media-and-publishing", "creators", "personal-brands"],
    relatedNeeds: [
      "stronger-brand",
      "content-system",
      "digital-product-setup",
      "strategy-before-deciding",
    ],
  },
  {
    slug: "local-detailing-brand",
    title: "Hedgy Hand Wash",
    client: "Hedgy Hand Wash",
    logo: "/portfolio/logos/hedgy-hand-wash.png",
    logoBg: "#FFFFFF",
    logoInk: "rgba(0,0,0,0.55)",
    type: "Owned Brand",
    category: "Local Business",
    industry: "local-business",
    summary:
      "How a local detailing concept was built to look bigger than one van and ready to expand into more service areas without a rebrand.",
    story: {
      problem:
        "The operator started with one van and one service area, but the goal was multi-city expansion within eighteen months. Every small detailing brand in the region looked like a hobby. This one had to look real from day one and hold up as a network of vans later.",
      sequence: [
        "Named and positioned the brand to work at both single-operator and multi-city scale.",
        "Designed the identity system so signage, vehicle wraps, and social all read the same across service areas.",
        "Built a website structured around service-area pages so each new city plugs in without a rebuild.",
        "Set up the Google Business Profile and launch materials for the first service area.",
      ],
      result:
        "The single-van operation launched looking like an early-stage network. Expansion into a second service area was a matter of adding a page and a profile, not a rebrand.",
    },
    cover: "/portfolio/placeholders/local-detailing-brand.jpg",
    assetStatus: "Anonymized cover pending; genericized project card.",
    scope: [
      "Brand Direction",
      "Local Service Positioning",
      "Website Direction",
      "Launch Support",
    ],
    relatedServices: [
      "brand-foundation-core",
      "website-growth-build",
      "local-visibility-starter-kit",
      "google-business-profile-setup",
    ],
    relatedIndustries: ["local-business", "automotive", "home-services"],
    relatedNeeds: [
      "launch-plan",
      "local-visibility",
      "stronger-brand",
      "better-website",
    ],
  },
];

export const getPortfolioBySlug = (slug) =>
  PORTFOLIO.find((p) => p.slug === slug);
